/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// @mui icons

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// sweetalert2 components
import Swal from "sweetalert2";

import { useSoftUIController, setActiveTemplate } from "context";

// Custom styles for the Configurator
import TemplatesApplierRoot from "views/TemplatesApplier/components/TemplatesApplierRoot";
import TemplateAccordion from "views/TemplatesApplier/components/TemplatesAccordion";

import { getData, putData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";

let selectedPlatformId = null;

function TemplatesApplier({
  templatesData,
  openTemplateApplier,
  handleCloseConfigurator,
  handleDataConfigurator,
}) {
  const abortController = useRef(null);

  const [controller, dispatch] = useSoftUIController();
  const { platform, sports, providers, activeTemplate } = controller;
  const [openDrawer, setOpenConfigurator] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [accordionData, setAccordionData] = useState([]);

  async function initData() {
    // console.log("init Templates Data", platform, sports);
    if (abortController.current) abortController.current.abort();
    abortController.current = new AbortController();

    let templates = [];
    // if (selectedPlatformId == platform.value) return;
    selectedPlatformId = platform.value;

    if (!selectedPlatformId) {
      //console.log("Not selected Platform!");
      return;
    }

    let getTemplatesEndpoint = `/api/v1/platforms/${selectedPlatformId}/templates`;
    let response = await getData(
      getTemplatesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController.current ? abortController.current : null
    );
    if (response != null) {
      templates = response.data.map((el) => {
        return {
          id: el.id,
          template: el.name,
          filters:
            el.providers_count +
            " Providers, " +
            (el.sports_count ? el.sports_count : 0) +
            " Sports, " +
            el.competitions_count +
            " Competitions, " +
            el.markets_disabled_count +
            " Disabled Markets",
          filterCategories: [
            {
              name: "Providers",
              value: el.providers
                ? el.providers.map(
                    (provider) => providers.find((item) => item.value == provider)["label"]
                  )
                : [],
              count: el.providers_count,
            },
            {
              name: "Sports",
              value: el.sports
                ? el.sports.map((sp) => sports.find((item) => item.id == sp)["name"])
                : [],
              count: el.sports_count,
            },
            {
              name: "Competitions",
              value: el.competitions
                ? el.competitions.map((competition) => {
                    return competition.name;
                  })
                : [],
              count: el.competitions_count,
            },
            {
              name: "Disabled Markets",
              value: el.markets_disabled_count > 0 ? new Array(el.markets_disabled_count) : [],
              count: el.markets_disabled_count,
            },
          ],
          status: el.status,
        };
      });
      let selectedTemplate = templates.find((template) => {
        return template.status === true;
      });
      if (selectedTemplate) {
        let selectedConfig = createAccordionData(
          selectedTemplate.id,
          selectedTemplate.template,
          selectedTemplate.filters,
          selectedTemplate.filterCategories,
          selectedTemplate.status
        );        handleDataConfigurator(selectedConfig, "preview");
        setActiveTemplate(dispatch, selectedConfig.id);
      } else {
        handleDataConfigurator(null, "preview");
      }
    } else {
      templates = [];
    }

    let accordionData = [];
    for (const key in templates) {
      if (Object.hasOwnProperty.call(templates, key)) {
        const template = templates[key];

        accordionData.push(
          createAccordionData(
            template.id,
            template.template,
            template.filters,
            template.filterCategories,
            template.status
          )
        );
      }
    }

    setAccordionData(accordionData);
  }

  function createAccordionData(
    id: number,
    name: string,
    info: string,
    details: array,
    status: boolean
  ) {
    return { id, name, info, details, status };
  }

  const handleChangeConfiguration = (selectedConfig, method) => {
    handleDataConfigurator(selectedConfig, method);
    if (method == "save") handleApplyAction(selectedConfig);
  };

  /**
   *
   * @param {*} template
   */
  const handleApplyAction = async (template) => {
    //console.log("id", template.id);
    let templateData = template;
    templateData.platform_id = platform.value;
    let templateToSend = {
      name: templateData.name,
      status: templateData.status === "Enabled" ? false : true,
      platform_id: templateData.platform_id,
    };

    function returnId() {
      return templateData.id;
    }

    function returnTemplateToSend() {
      return templateToSend;
    }

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let setTemplatesEndpoint = `/api/v1/templates/${returnId()}`;

        let response = await putData(setTemplatesEndpoint, returnTemplateToSend(), {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      initData();
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  useEffect(() => {
    setOpenConfigurator(openTemplateApplier);
  }, [openTemplateApplier]);

  useEffect(() => {
    if (platform && sports) initData();
  }, [platform, sports]);

  useEffect(() => {
    return () => {
      // console.log("living monitoring page...");
      if (abortController.current) {
        // console.log("abortController monitoring page");
        abortController.current.abort();
      }
    };
  }, []);

  return (
    <TemplatesApplierRoot variant="permanent" ownerState={openDrawer}>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
      >
        <SoftBox>
          <SoftTypography variant="h5">Template Preview</SoftTypography>
          <SoftTypography variant="body2" color="text">
            Preview saved Templates on feed.
          </SoftTypography>
        </SoftBox>

        <Icon
          sx={({ typography: { size, fontWeightBold }, palette: { dark } }) => ({
            fontSize: `${size.md} !important`,
            fontWeight: `${fontWeightBold} !important`,
            stroke: dark.main,
            strokeWidth: "2px",
            cursor: "pointer",
            mt: 2,
          })}
          onClick={(e) => handleCloseConfigurator(e)}
        >
          close
        </Icon>
      </SoftBox>

      <Divider />

      <SoftBox pt={1.25} pb={3} px={3}>
        <TemplateAccordion
          data={accordionData}
          handleChangeConfiguration={handleChangeConfiguration}
        ></TemplateAccordion>
      </SoftBox>
    </TemplatesApplierRoot>
  );
}

// Typechecking props for the DataTable
TemplatesApplier.propTypes = {
  templatesData: PropTypes.array,
  openTemplateApplier: PropTypes.bool,
  handleCloseConfigurator: PropTypes.func,
  handleDataConfigurator: PropTypes.func,
};

export default TemplatesApplier;
